import React, { useEffect, useState } from "react";

import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import { makeStyles } from "@material-ui/core/styles";
import { Equalizer } from "@material-ui/icons";
import { toast } from "react-toastify";

import useSettings from "../../hooks/useSettings";
import { i18n } from "../../translate/i18n";
import CronBuilder from "../CronBuilder";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
  },
  sectionPaper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(1),
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  sectionIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  sectionTitle: {
    fontWeight: 600,
  },
  sectionSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    marginTop: theme.spacing(0.5),
  },
}));

export default function CompanyUsageSettings(props) {
  const { settings } = props;
  const classes = useStyles();
  const { update } = useSettings();

  const [companyUsageCron, setCompanyUsageCron] = useState("0 3 * * *");
  const [incrementalEnabled, setIncrementalEnabled] = useState(true);

  useEffect(() => {
    if (Array.isArray(settings) && settings.length) {
      const cron =
        settings.find((s) => s.key === "companyUsageCron")?.value ||
        "0 3 * * *";
      const incremental =
        settings.find((s) => s.key === "companyUsageIncremental")?.value;
      setCompanyUsageCron(cron);
      setIncrementalEnabled(incremental !== "disabled");
    }
  }, [settings]);

  const handleSave = async (cron) => {
    setCompanyUsageCron(cron);
    await update({ key: "companyUsageCron", value: cron });
    toast.success(i18n.t("settings.success"));
  };

  const handleToggleIncremental = async (e) => {
    const enabled = e.target.checked;
    setIncrementalEnabled(enabled);
    await update({
      key: "companyUsageIncremental",
      value: enabled ? "enabled" : "disabled",
    });
    toast.success(i18n.t("settings.success"));
  };

  return (
    <div className={classes.container}>
      <Paper className={classes.sectionPaper}>
        <div className={classes.sectionHeader}>
          <Equalizer className={classes.sectionIcon} />
          <div>
            <Typography variant="h6" className={classes.sectionTitle}>
              {i18n.t("companyUsage.settings.title")}
            </Typography>
            <Typography className={classes.sectionSubtitle}>
              {i18n.t("companyUsage.settings.description")}
            </Typography>
          </div>
        </div>

        <Grid container spacing={3}>
          <Grid xs={12} sm={6} item>
            <CronBuilder
              value={companyUsageCron}
              label={i18n.t("companyUsage.settings.cronLabel")}
              onChange={handleSave}
            />
          </Grid>
          <Grid xs={12} sm={6} item>
            <FormControlLabel
              control={
                <Switch
                  checked={incrementalEnabled}
                  onChange={handleToggleIncremental}
                  color="primary"
                />
              }
              label={i18n.t("companyUsage.settings.incrementalLabel")}
            />
            <Typography
              variant="caption"
              display="block"
              color="textSecondary"
            >
              {i18n.t("companyUsage.settings.incrementalHelp")}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
}
